import React, { memo, useContext, useEffect } from 'react';
import { Blok } from '../../interfaces/Blok.interface';
import { Theme } from '../../interfaces/enums/Theme.enum';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import QuickQuoteCTA from './QuickQuoteCTA';
import { QuickQuoteContext, Service } from '../../contexts/QuickQuote.context';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';
import { incrementDataLayerEvent } from '../../utils/pushToDataLayer/pushToDataLayer.util';
import { getDataLayerQuoteFunnerJourney } from '../../utils/getDataLayerQuoteFunnelJourney/getDataLayerQuoteFunnelJourney.util';
import { ProductType } from '../../services/quote.interfaces';

export interface QuickQuoteWrapperProps extends Blok {
  content: React.ReactNode[]; // Array of JSX elements
  heading: string;
  buy_field_name?: string;
  sell_field_name?: string;
  buy_and_sell_field_name?: string;
  remortgage_field_name?: string;
  columns: number;
  isBuying?: boolean;
  isSelling?: boolean;
  name?: string;
  inView?: boolean;
}

const QuickQuoteWrapper: React.FC<QuickQuoteWrapperProps> = ({
  content,
  heading,
  buy_field_name,
  sell_field_name,
  buy_and_sell_field_name,
  remortgage_field_name,
  isBuying,
  isSelling,
  columns = 2,
  name,
  inView,
}) => {
  const { service, quoteStep } = useContext(QuickQuoteContext);
  const chosenService =
    service === Service.BUY
      ? buy_field_name
      : service === Service.SELL
      ? sell_field_name
      : service === Service.BUY_AND_SELL
      ? buy_and_sell_field_name
      : service === Service.REMORTGAGE
      ? 'remortgaging'
      : '';

  const isBuyingSellingService = service === Service.BUY_AND_SELL;
  const BuyingOrSellingHeading = isBuying
    ? heading.replace('<service>', 'buying')
    : isSelling && heading.replace('<service>', 'selling');

  useEffect(() => {
    if (inView) {
      incrementDataLayerEvent({
        event: 'embedded_quote_view',
        data: {
          embedded_quote_step: `step${quoteStep + 1}`,
          funnel_journey: getDataLayerQuoteFunnerJourney(
            service as ProductType
          ),
        },
      });
    }
  }, [inView]);

  return (
    <div>
      <h3 className="text-xl md:text-3xl text-center pb-6 pt-10">
        {isBuyingSellingService
          ? BuyingOrSellingHeading || heading
          : heading.replace('<service>', chosenService?.toLowerCase() || '')}
      </h3>
      <div className="grid grid-cols-8 gap-x-6 gap-y-4">
        {content.map((item, idx) => (
          <div
            className={classNames('h-full', {
              'col-span-8 lg:col-span-4': columns === 2,
              'col-span-8 lg:col-span-6 lg:col-start-2': columns === 1,
            })}
            key={`service-${idx}-${name}`}
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  );
};

export default memo(withBlokWrapper(QuickQuoteWrapper, Theme.ALT));
